import React, { useMemo, useState, useCallback, useRef, useLayoutEffect, useEffect, useId } from 'react';
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, ReferenceLine, CartesianGrid } from 'recharts';
import type { CycleRing } from '@/lib/cycleTypes';
import { cn } from '@/lib/utils';
import { Activity } from 'lucide-react';
import { getLeapCyclePosition } from '@/lib/cycleConfig';
import { Button } from '@/components/ui/button';

const SYNODIC_MONTH = 29.53059;
const KNOWN_FULL_MOON = new Date('2026-05-15T16:00:00Z').getTime();
const SWIPE_DEADZONE_PX = 8;
const LEGEND_LONG_PRESS_MS = 550;

const normalizePhase = (value: number) => ((value % 1) + 1) % 1;

const wrapDayOfYear = (dayOfYearFloat: number, maxDay = 365) => {
  const wrapped = ((dayOfYearFloat - 1) % maxDay + maxDay) % maxDay + 1;
  return wrapped;
};

const getWaveformValue = (phase: number, waveform: CycleRing['waveform']): number => {
  const normalized = normalizePhase(phase);
  switch (waveform) {
    case 'square':
      return normalized < 0.5 ? 1 : 0;
    case 'triangle':
      return 1 - Math.abs(normalized * 2 - 1);
    case 'sawtooth':
    case 'default':
      return normalized;
    case '24hr':
    case 'sine':
    default:
      return (Math.sin(normalized * 2 * Math.PI - Math.PI / 2) + 1) / 2;
  }
};

const getMoonPhaseForDate = (date: Date) => {
  const daysSinceFullMoon = (date.getTime() - KNOWN_FULL_MOON) / (1000 * 60 * 60 * 24);
  let phase = ((daysSinceFullMoon / SYNODIC_MONTH) + 0.5) % 1;
  if (phase < 0) phase += 1;
  return phase;
};

const getDateForDayOfYear = (dayOfYearFloat: number, year: number) => {
  const wrappedDay = wrapDayOfYear(dayOfYearFloat);
  const wholeDay = Math.floor(wrappedDay);
  const fractionOfDay = wrappedDay - wholeDay;
  const base = new Date(Date.UTC(year, 0, 1));
  base.setUTCDate(wholeDay);
  const totalSeconds = fractionOfDay * 86400;
  base.setUTCHours(0, 0, 0, 0);
  base.setUTCSeconds(totalSeconds);
  return { date: base, fractionOfDay };
};

const ringColorFallbacks: Record<string, string> = {
  lunar: 'hsl(220, 60%, 70%)',
  moons: 'hsl(280, 60%, 60%)',
  days: 'hsl(45, 80%, 60%)',
  seals: 'hsl(0, 70%, 60%)',
  colors: 'hsl(160, 60%, 50%)',
  plasmas: 'hsl(320, 70%, 60%)',
};

const getStableRingFallback = (ringId: string) => {
  const mapped = ringColorFallbacks[ringId];
  if (mapped) return mapped;

  const seed = ringId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const hue = (seed * 53) % 360;
  return `hsl(${hue},60%,60%)`;
};

const getRingStroke = (ring: CycleRing, _index: number) => {
  const explicit = ring.colors?.[ring.currentStage ?? 0] ?? ring.colors?.[0];
  if (explicit) return `hsl(${explicit})`;
  return getStableRingFallback(ring.id);
};

const parseHslComponents = (value: string) => {
  const cleaned = value.trim().replace(/^hsl\(/i, '').replace(/\)$/i, '').split('/')[0].trim();
  const match = cleaned.match(/^(-?\d+(?:\.\d+)?)\s+(-?\d+(?:\.\d+)?)%?\s+(-?\d+(?:\.\d+)?)%?$/);
  if (!match) return null;
  return {
    h: Number(match[1]),
    s: Number(match[2]),
    l: Number(match[3]),
  };
};

const interpolateStageColor = (current: string, next: string, t: number) => {
  const a = parseHslComponents(current);
  const b = parseHslComponents(next);
  if (!a || !b) return `hsl(${current})`;
  const ratio = Math.max(0, Math.min(1, t));
  const hueDelta = ((((b.h - a.h) % 360) + 540) % 360) - 180;
  const h = (a.h + hueDelta * ratio + 360) % 360;
  const s = a.s + (b.s - a.s) * ratio;
  const l = a.l + (b.l - a.l) * ratio;
  return `hsl(${h.toFixed(2)} ${s.toFixed(2)}% ${l.toFixed(2)}%)`;
};

const getLineType = (waveform: CycleRing['waveform']) => {
  switch (waveform) {
    case 'square':
      return 'stepAfter' as const;
    case 'triangle':
    case 'sawtooth':
    case 'default':
      return 'linear' as const;
    case '24hr':
    case 'sine':
    default:
      return 'monotone' as const;
  }
};

interface ShortWaveformWidgetProps {
  rings: CycleRing[];
  allRings?: CycleRing[];
  hiddenRings?: string[];
  currentDayOfYear: number;
  year?: number;
  onDayChange?: (day: number, fractionOfDay: number) => void;
  onInteractionStart?: () => void;
  onToggleRing?: (ringId: string) => void;
  rangeDaysOverride?: number;
  onRangeChange?: (days: number) => void;
  className?: string;
}

export function ShortWaveformWidget({
  rings,
  allRings,
  hiddenRings = [],
  currentDayOfYear,
  year = 2025,
  onDayChange,
  onInteractionStart,
  onToggleRing,
  rangeDaysOverride,
  onRangeChange,
  className,
}: ShortWaveformWidgetProps) {
  const gradientPrefix = useId().replace(/[:]/g, '');
  interface DragAnchor {
    clientX: number;
    dayAtStart: number;
    spanDays: number;
    chartWidth: number;
  }

  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [rangeDaysInternal, setRangeDaysInternal] = useState<number>(7);
  const [selectedRingIds, setSelectedRingIds] = useState<string[]>([]);
  const [showAllLegendItems, setShowAllLegendItems] = useState(false);
  const rangeDays = rangeDaysOverride ?? rangeDaysInternal;
  const isDraggingRef = useRef(false);
  const dragAnchorRef = useRef<DragAnchor | null>(null);
  const [previewDay, setPreviewDay] = useState<number | null>(null);
  const legendLongPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const legendLongPressTriggeredRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isDraggingRef.current) {
      setPreviewDay(null);
    }
  }, [currentDayOfYear]);

  const displayDayOfYear = previewDay ?? currentDayOfYear;

  useLayoutEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    const updateSize = () => setContainerWidth(element.getBoundingClientRect().width);
    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const visibleRings = useMemo(
    () => rings.filter((ring) => ring && ring.id && !hiddenRings.includes(ring.id)),
    [rings, hiddenRings]
  );

  useEffect(() => {
    if (!visibleRings.length) {
      setSelectedRingIds([]);
      return;
    }
    setSelectedRingIds((prev) => {
      const next = prev.filter((ringId) => visibleRings.some((ring) => ring.id === ringId));
      return next.length > 0 ? next : visibleRings.slice(0, 4).map((ring) => ring.id);
    });
  }, [visibleRings]);

  const legendRings = useMemo(
    () => (allRings && allRings.length > 0 ? allRings : rings).filter((ring) => ring && ring.id),
    [allRings, rings]
  );

  const colorById = useMemo(() => {
    const map = new Map<string, string>();
    legendRings.forEach((ring, index) => {
      map.set(ring.id, getRingStroke(ring, index));
    });
    return map;
  }, [legendRings]);

  const ringMap = useMemo(() => {
    const source = allRings && allRings.length > 0 ? allRings : rings;
    return new Map(source.map((ring) => [ring.id, ring]));
  }, [allRings, rings]);

  const visibleRange = useMemo(() => {
    const halfRange = rangeDays / 2;
    const start = displayDayOfYear - halfRange;
    const end = displayDayOfYear + halfRange;
    return { start, end };
  }, [displayDayOfYear, rangeDays]);

  const resolveExactPosition = useCallback(
    (ring: CycleRing, dayOfYearFloat: number) => {
      const cache = new Map<string, number>();

      const resolve = (target: CycleRing): number => {
        if (cache.has(target.id)) {
          return cache.get(target.id)!;
        }

        const stages = Math.max(1, target.stages ?? 1);
        const wrappedDay = wrapDayOfYear(dayOfYearFloat);
        const wholeDay = Math.floor(wrappedDay);
        const fractionOfDay = wrappedDay - wholeDay;
        const isDrift = wholeDay === 365;
        const dayInMonth = isDrift ? 0 : ((wholeDay - 1) % 28) + 1;
        const monthNum = isDrift ? 13 : Math.floor((wholeDay - 1) / 28) + 1;

        let exactPosition: number;

        if (target.id === 'days') {
          exactPosition = isDrift ? 27.5 + fractionOfDay : dayInMonth - 1 + fractionOfDay;
        } else if (target.id === 'moons' || (target.stages === 13 && (target.name ?? '').toLowerCase().includes('moon'))) {
          if (isDrift) {
            exactPosition = 12.5 + fractionOfDay;
          } else {
            const monthProgress = (dayInMonth - 1 + fractionOfDay) / 28;
            exactPosition = monthNum - 1 + monthProgress;
          }
        } else if (target.id === 'gregorian-leap') {
          const { date, fractionOfDay: frac } = getDateForDayOfYear(dayOfYearFloat, year);
          const { stageIndex, progress } = getLeapCyclePosition(date, frac);
          exactPosition = stageIndex + progress;
        } else if (target.id === 'lunar') {
          const { date } = getDateForDayOfYear(dayOfYearFloat, year);
          const phase = getMoonPhaseForDate(date);
          exactPosition = phase * stages;
        } else if (target.parentId && target.stagesPerParent) {
          if (target.parentId === '__timeline__') {
            exactPosition = (dayOfYearFloat - 1) * target.stagesPerParent;
          } else {
            const parentRing = ringMap.get(target.parentId);
            if (parentRing) {
              const parentPos = resolve(parentRing);
              exactPosition = parentPos * target.stagesPerParent;
            } else {
              const fallback = (dayOfYearFloat - 1) % stages;
              exactPosition = fallback < 0 ? fallback + stages : fallback;
            }
          }
        } else {
          const base = (dayOfYearFloat - 1) % stages;
          exactPosition = base < 0 ? base + stages : base;
        }

        cache.set(target.id, exactPosition);
        return exactPosition;
      };

      return resolve(ring);
    },
    [ringMap, year]
  );

  const getCycleValueForDay = useCallback(
    (ring: CycleRing, dayOfYearFloat: number) => {
      const isLunarRing = ring.id === 'lunar' || (ring.name ?? '').toLowerCase().includes('lunar');
      if (isLunarRing) {
        const { date } = getDateForDayOfYear(dayOfYearFloat, year);
        const lunarPhase = getMoonPhaseForDate(date);
        // Illumination curve: new moon=0, full moon=1
        return (1 - Math.cos(lunarPhase * 2 * Math.PI)) / 2;
      }
      const stages = Math.max(1, ring.stages ?? 1);
      if (ring.useCurrentStageAsValue && ring.currentStage !== undefined) {
        const safeDenominator = stages > 1 ? stages - 1 : 1;
        const rawValue = ring.currentStage / safeDenominator;
        return Math.min(1, Math.max(0, rawValue));
      }
      const exactPosition = resolveExactPosition(ring, dayOfYearFloat);
      const basePhase = normalizePhase(exactPosition / stages);
      const multiplier = ring.waveformIntervalMultiplier ?? 1;
      const divider = ring.waveformIntervalDivider ?? 1;
      const safeDivider = divider === 0 ? 1 : divider;
      const phase = (basePhase * multiplier) / safeDivider;
      return getWaveformValue(phase, ring.waveform ?? 'default');
    },
    [resolveExactPosition, year]
  );

  const getStageColorForDay = useCallback(
    (ring: CycleRing, dayOfYearFloat: number, index: number) => {
      const palette = (ring.colors ?? []).filter((value): value is string => typeof value === 'string' && value.trim().length > 0);
      if (!palette.length) return getRingStroke(ring, index);
      const stages = Math.max(1, ring.stages ?? palette.length);
      const exactPosition = resolveExactPosition(ring, dayOfYearFloat);
      const baseIndex = Math.floor(exactPosition);
      const stageIndex = ((baseIndex % stages) + stages) % stages;
      const nextStageIndex = (stageIndex + 1) % stages;
      const blend = exactPosition - baseIndex;
      const currentColor = palette[stageIndex % palette.length];
      const nextColor = palette[nextStageIndex % palette.length];
      return interpolateStageColor(currentColor, nextColor, blend);
    },
    [resolveExactPosition]
  );

  const span = useMemo(() => Math.max(0.01, visibleRange.end - visibleRange.start), [visibleRange]);

  const data = useMemo(() => {
    if (!visibleRings.length) return [];
    const plottedRings = visibleRings.filter((ring) => selectedRingIds.includes(ring.id));
    if (!plottedRings.length) return [];
    const samplesPerDay = rangeDays <= 1 ? 48 : rangeDays <= 7 ? 12 : 6;
    const totalSamples = Math.max(2, Math.min(200, Math.ceil(span * samplesPerDay)));
    const step = span / Math.max(1, totalSamples - 1);

    const points: Array<Record<string, number>> = [];
    for (let i = 0; i < totalSamples; i++) {
      const day = visibleRange.start + i * step;
      const row: Record<string, number> = { day };
      plottedRings.forEach((ring) => {
        row[ring.id] = getCycleValueForDay(ring, day);
      });
      points.push(row);
    }
    return points;
  }, [visibleRings, selectedRingIds, visibleRange.start, span, getCycleValueForDay, rangeDays]);

  const selectedRings = useMemo(
    () => visibleRings.filter((ring) => selectedRingIds.includes(ring.id)),
    [selectedRingIds, visibleRings]
  );

  const legendHasMoreItems = visibleRings.length > 4;
  const legendDisplayRings = useMemo(
    () => (showAllLegendItems ? visibleRings : visibleRings.slice(0, 4)),
    [showAllLegendItems, visibleRings]
  );

  useEffect(() => {
    if (!legendHasMoreItems && showAllLegendItems) {
      setShowAllLegendItems(false);
    }
  }, [legendHasMoreItems, showAllLegendItems]);

  const getDayFromClientX = useCallback(
    (clientX: number) => {
      const element = containerRef.current;
      if (!element) return currentDayOfYear;
      const rect = element.getBoundingClientRect();
      const chartMarginLeft = 8;
      const chartMarginRight = 8;
      const chartWidth = Math.max(1, rect.width - chartMarginLeft - chartMarginRight);
      const ratio = Math.max(0, Math.min(1, (clientX - rect.left - chartMarginLeft) / chartWidth));
      const spanDays = Math.max(0.01, visibleRange.end - visibleRange.start);
      return wrapDayOfYear(visibleRange.start + ratio * spanDays);
    },
    [visibleRange, currentDayOfYear]
  );

  // Keep latest reactive values accessible inside the non-reactive wheel event handler
  const wheelStateRef = useRef<{
    displayDayOfYear: number;
    visibleRange: { start: number; end: number };
    onDayChange: ((day: number, fraction: number) => void) | undefined;
    onInteractionStart: (() => void) | undefined;
  }>({ displayDayOfYear, visibleRange, onDayChange, onInteractionStart });
  wheelStateRef.current = { displayDayOfYear, visibleRange, onDayChange, onInteractionStart };

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const handler = (e: WheelEvent) => {
      const { onDayChange: cb, onInteractionStart: onStart, displayDayOfYear: day, visibleRange: range } =
        wheelStateRef.current;
      if (!cb) return;
      e.preventDefault();
      onStart?.();
      const spanDays = Math.max(0.01, range.end - range.start);
      const width = Math.max(1, el.getBoundingClientRect().width - 16);
      const rawDelta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
      const deltaDays = (rawDelta / width) * spanDays;
      const nextDay = wrapDayOfYear(day + deltaDays);
      cb(Math.floor(nextDay), nextDay - Math.floor(nextDay));
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // mount once — reads latest values via wheelStateRef

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (!onDayChange) return;
      onInteractionStart?.();
      e.preventDefault();
      isDraggingRef.current = true;
      e.currentTarget.setPointerCapture(e.pointerId);
      const element = containerRef.current;
      if (!element) return;
      const rect = element.getBoundingClientRect();
      const chartMarginLeft = 8;
      const chartMarginRight = 8;
      const chartWidth = Math.max(1, rect.width - chartMarginLeft - chartMarginRight);
      dragAnchorRef.current = {
        clientX: e.clientX,
        dayAtStart: displayDayOfYear,
        spanDays: Math.max(0.01, visibleRange.end - visibleRange.start),
        chartWidth,
      };
    },
    [displayDayOfYear, onDayChange, onInteractionStart, visibleRange.end, visibleRange.start]
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!isDraggingRef.current || !onDayChange) return;
      const anchor = dragAnchorRef.current;
      if (!anchor) {
        const day = getDayFromClientX(e.clientX);
        setPreviewDay(day);
        return;
      }

      const deltaX = e.clientX - anchor.clientX;
      const deltaDays = (deltaX / anchor.chartWidth) * anchor.spanDays;
      const nextDay = wrapDayOfYear(
        Math.abs(deltaX) < SWIPE_DEADZONE_PX ? anchor.dayAtStart : anchor.dayAtStart + deltaDays
      );
      setPreviewDay(nextDay);
      onDayChange(Math.floor(nextDay), nextDay - Math.floor(nextDay));
    },
    [getDayFromClientX, onDayChange]
  );

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    if (isDraggingRef.current && onDayChange && previewDay !== null) {
      onDayChange(Math.floor(previewDay), previewDay - Math.floor(previewDay));
    }
    isDraggingRef.current = false;
    dragAnchorRef.current = null;
    e.currentTarget.releasePointerCapture(e.pointerId);
  }, [onDayChange, previewDay]);

  const rangeOptions: Array<{ label: string; days: number }> = useMemo(() => {
    return [
      { label: 'D', days: 1 },
      { label: 'W', days: 7 },
      { label: '2W', days: 14 },
      { label: 'M', days: 28 },
      { label: '1Y', days: 365 },
    ];
  }, []);

  const indicatorLeft = useMemo(() => {
    if (!containerWidth) return '50%';
    const spanDays = Math.max(0.01, visibleRange.end - visibleRange.start);
    const clampedDay = Math.min(visibleRange.end, Math.max(visibleRange.start, displayDayOfYear));
    const ratio = (clampedDay - visibleRange.start) / spanDays;
    return `${ratio * 100}%`;
  }, [containerWidth, displayDayOfYear, visibleRange]);

  const waveformDateFormatter = useMemo(
    () => new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
    []
  );

  const formatWaveformTick = useCallback(
    (value: number) => {
      const day = Math.floor(wrapDayOfYear(value));
      const date = new Date(Date.UTC(year, 0, 1));
      date.setUTCDate(day);
      return waveformDateFormatter.format(date);
    },
    [waveformDateFormatter, year]
  );

  const cancelLegendLongPress = useCallback(() => {
    if (legendLongPressTimerRef.current) {
      clearTimeout(legendLongPressTimerRef.current);
      legendLongPressTimerRef.current = null;
    }
  }, []);

  const startLegendLongPress = useCallback(
    (ringId: string) => {
      cancelLegendLongPress();
      legendLongPressTriggeredRef.current = null;
      legendLongPressTimerRef.current = setTimeout(() => {
        legendLongPressTriggeredRef.current = ringId;
        setSelectedRingIds([ringId]);
        legendLongPressTimerRef.current = null;
      }, LEGEND_LONG_PRESS_MS);
    },
    [cancelLegendLongPress]
  );

  useEffect(() => () => cancelLegendLongPress(), [cancelLegendLongPress]);

  const legendButtons = (
    <div className="flex items-center gap-2 overflow-x-auto text-[9px] min-w-0">
      {legendDisplayRings.map((ring, index) => {
        const color = colorById.get(ring.id) ?? getRingStroke(ring, index);
        const isSelected = selectedRingIds.includes(ring.id);
        return (
          <button
            key={ring.id}
            type="button"
            onClick={() => {
              if (legendLongPressTriggeredRef.current === ring.id) {
                legendLongPressTriggeredRef.current = null;
                return;
              }
              cancelLegendLongPress();
              setSelectedRingIds((prev) => (
                prev.includes(ring.id)
                  ? prev.filter((ringId) => ringId !== ring.id)
                  : [...prev, ring.id]
              ));
            }}
            onPointerDown={(event) => {
              if (event.pointerType === 'mouse' && event.button !== 0) return;
              startLegendLongPress(ring.id);
            }}
            onPointerUp={cancelLegendLongPress}
            onPointerLeave={cancelLegendLongPress}
            onPointerCancel={cancelLegendLongPress}
            onTouchStart={() => startLegendLongPress(ring.id)}
            onTouchEnd={cancelLegendLongPress}
            onTouchCancel={cancelLegendLongPress}
            className={cn(
              "flex items-center gap-1 shrink-0 rounded px-1 py-0.5 transition-colors",
              isSelected ? "bg-white/10 text-purple-100" : "text-purple-200/70 hover:bg-white/5 hover:text-purple-100"
            )}
            aria-label={`${isSelected ? 'Hide' : 'Show'} ${ring.name ?? ring.id} waveform`}
            title={`${ring.name ?? ring.id} (long press to solo)`}
          >
            <div
              className="w-2 h-2 rounded-sm"
              style={{ backgroundColor: color }}
            />
            <span className="truncate max-w-[72px]">
              {ring.name ?? ring.id}
            </span>
          </button>
        );
      })}
    </div>
  );

  return (
    <div className={cn("group relative w-full h-full flex flex-col px-2 py-0", className)}>
      {/* Chart area - simplified for short widget */}
      <div
        ref={containerRef}
        className={cn(
          "relative flex-1 min-h-[60px] overflow-hidden",
          onDayChange && "cursor-crosshair touch-none select-none"
        )}
        tabIndex={onDayChange ? 0 : -1}
        style={{
          background: 'transparent',
          boxShadow: 'none',
        }}
        onFocus={onInteractionStart}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        {visibleRings.length === 0 ? (
          <div className="absolute inset-0 flex items-center justify-center text-[10px] text-purple-300/40">
            No cycles
          </div>
        ) : (
          <div
            className="h-full w-full"
          >
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data} margin={{ top: 0, right: 4, bottom: 14, left: 4 }}>
                <defs>
                  {selectedRings.map((ring, index) => {
                    const gradientId = `${gradientPrefix}-${ring.id}-line-gradient`;
                    const sampleCount = Math.max(2, Math.min(80, Math.ceil(span * 8)));
                    const gradientColors = Array.from({ length: sampleCount }, (_, sampleIndex) => {
                      const t = sampleCount === 1 ? 0 : sampleIndex / (sampleCount - 1);
                      const day = visibleRange.start + t * span;
                      return getStageColorForDay(ring, day, index);
                    });
                    return (
                      <linearGradient key={gradientId} id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
                        {gradientColors.map((color, colorIndex) => {
                          const offset = gradientColors.length === 1
                            ? 0
                            : (colorIndex / (gradientColors.length - 1)) * 100;
                          return <stop key={`${gradientId}-stop-${colorIndex}`} offset={`${offset}%`} stopColor={color} />;
                        })}
                      </linearGradient>
                    );
                  })}
                </defs>
                <YAxis
                  type="number"
                  domain={[0, 1]}
                  hide
                />
                <XAxis
                  dataKey="day"
                  type="number"
                  domain={[visibleRange.start, visibleRange.end]}
                  hide={false}
                  tickLine={false}
                  axisLine={false}
                  tick={{ fill: 'rgba(255,255,255,0.65)', fontSize: 9 }}
                  tickFormatter={formatWaveformTick}
                  minTickGap={22}
                />
                <ReferenceLine
                  y={0}
                  stroke="rgba(255,255,255,0.2)"
                  strokeWidth={1}
                />
                <ReferenceLine 
                  x={displayDayOfYear} 
                  stroke="rgba(236,72,153,0.82)" 
                  strokeWidth={2}
                  strokeDasharray="2 2"
                />
                {selectedRings.map((ring, index) => (
                  <Line
                    key={ring.id}
                    type={getLineType(ring.waveform)}
                    dataKey={ring.id}
                    stroke={`url(#${gradientPrefix}-${ring.id}-line-gradient)`}
                    strokeWidth={2.6}
                    dot={false}
                    isAnimationActive={false}
                    style={{ filter: 'drop-shadow(0 0 4px currentColor)' }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
        
        {/* Now indicator - hidden */}
        <div
          className="hidden"
          style={{
            left: indicatorLeft,
          }}
        />
      </div>

      <div className="flex items-center gap-1 pt-0.5 pb-0.5 min-h-0 shrink-0">
        <div className="pointer-events-auto min-w-0 flex-1 overflow-x-auto">{legendButtons}</div>
        <div className="pointer-events-auto shrink-0 flex items-center gap-0.5 border-l border-white/10 pl-1 ml-0.5">
          {rangeOptions.map((opt) => (
            <button
              key={opt.days}
              onClick={() => {
                if (!rangeDaysOverride) setRangeDaysInternal(opt.days);
                onRangeChange?.(opt.days);
              }}
              className={cn(
                "px-1 py-0.5 text-[9px] rounded transition-all font-medium",
                rangeDays === opt.days
                  ? "bg-purple-500/30 text-purple-200 border border-purple-400/50"
                  : "text-purple-300/60 hover:text-purple-200"
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
